import {
  isTerminal,
  type AiPhotoScope,
  type CreatePredictionResponse,
  type PredictionResponse,
} from '@ai-photo-style/shared';
import { ApiClient } from './api';
import { EmbedError, toEmbedError, type ErrorCode } from './errors';
import { compressForUpload, preparePhoto, type PreparedPhoto } from './image';
import { getIllustration, rememberIllustration, listIllustrations, matchesLibraryScope, type LibraryRecord } from './library';
import { TurnstileSession } from './turnstile';
import * as kc from './kc';
import { setTrackedStyle, track } from './analytics';
import { log, type EmbedConfig } from './config';
import { originScope, presentLook, resolveCatalog, type ResolvedCatalog } from './catalog';

export interface ResultImage {
  blob: Blob;
  url: string;
  width: number;
  height: number;
}

export type Step =
  | { name: 'intro' }
  | { name: 'preparing' }
  | { name: 'style'; photo: PreparedPhoto }
  | { name: 'working'; photo: PreparedPhoto; styleId: string; phase: 'uploading' | 'painting'; startedAt: number }
  | { name: 'compare'; photo: PreparedPhoto; styleId: string; result: ResultImage }
  | { name: 'applying'; photo?: PreparedPhoto; result: ResultImage }
  | { name: 'done'; cardPreviewUrl: string }
  | {
      name: 'error';
      code: ErrorCode;
      retryAfterS?: number;
      photo?: PreparedPhoto;
      styleId?: string;
      result?: ResultImage;
      /** Wat "Probeer opnieuw" doet. */
      retry: 'generate' | 'apply' | 'pick';
    };

export interface LibraryCard {
  id: string;
  styleId: string;
  label: string;
  width: number;
  height: number;
  createdAt: number;
  previewUrl: string;
}

export interface FlowState {
  open: boolean;
  step: Step;
  /** Staat er een illustratie op het kaartje van deze order? */
  applied: boolean;
  /** Eerder gemaakte illustraties in deze browser, nieuwste eerst. */
  library: LibraryCard[];
}

const POLL_INTERVAL_MS = 2000;
// Iets ruimer dan de backend (300 s): modellen zijn soms veel trager dan normaal.
const POLL_TIMEOUT_MS = 330_000;

export class Flow {
  state: FlowState;
  readonly catalog: ResolvedCatalog;
  readonly scope: AiPhotoScope;
  /** Wordt door de UI gezet; hier rendert Turnstile (onzichtbaar tenzij interactie nodig is). */
  turnstileContainer: HTMLElement | null = null;

  private readonly listeners = new Set<() => void>();
  private readonly api: ApiClient;
  private readonly sessionId = newSessionId();
  private turnstile: TurnstileSession | null = null;
  private nonce: { value: string; usesLeft: number; expiresAt: number } | null = null;
  private noncePromise: Promise<string> | null = null;
  private work: AbortController | null = null;
  /** Invalideert fotoverwerking bij sluiten, annuleren of het kiezen van een andere foto. */
  private photoReadId = 0;
  private predictionId: string | null = null;
  private historyPushed = false;
  private previewUrls: string[] = [];
  private catalogStale = false;

  constructor(
    private readonly cfg: EmbedConfig,
    private readonly coid: string,
  ) {
    this.catalog = resolveCatalog(cfg);
    const scope = this.catalog.catalog ?? originScope(location.origin) ?? { shopId: 'hippe', environmentId: 'production' };
    this.scope = { shopId: scope.shopId, environmentId: scope.environmentId, configId: cfg.configId };
    this.api = new ApiClient(cfg.apiBase, cfg.mockScenario, cfg.modelOverride, cfg.openaiBilling, this.catalog.catalog?.revision, cfg.configId);
    this.state = { open: false, step: { name: 'intro' }, applied: kc.isApplied(coid, this.scope), library: [] };
    void this.refreshLibrary();
    window.addEventListener('popstate', () => {
      // Android-terugknop: sluit de modal in plaats van de pagina te verlaten.
      if (this.state.open && this.historyPushed) {
        this.historyPushed = false;
        this.closeInternal();
      }
    });
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get shopId(): string {
    return this.scope.shopId;
  }

  get appliedCoid(): string | null {
    return this.state.applied ? this.coid : null;
  }

  /** `scenes` toont de scènetool; anders de vier stijlen. */
  get variant(): 'stijlen' | 'scenes' {
    return this.catalog.variant;
  }

  get choices() {
    return this.catalog.choices;
  }

  /** Afbeelding op de productpaginaknop. Leeg betekent de ingebouwde illustratie. */
  get ctaImageUrl(): string | undefined {
    return this.catalog.catalog?.ctaImageUrl ?? this.cfg.ctaImageUrl;
  }

  get availabilityError(): ErrorCode | undefined {
    return this.catalogStale ? 'CATALOG_CHANGED' : this.catalog.error;
  }

  lookLabel(id: string): string {
    const look = this.catalog.looks.find((look) => look.id === id);
    return look ? (this.cfg.configId !== undefined ? look : presentLook(look, this.variant)).label : id;
  }

  /** Na herladen: de eerder gemaakte aquarel is op de nieuwe order teruggezet. */
  markRestored(): void {
    this.set({ applied: true });
  }

  /** Haalt een eerder gemaakte illustratie op via zijn prediction-id (ook voor support: #aps_reuse=<id>). */
  async importPrediction(id: string): Promise<boolean> {
    if (this.catalog.error === 'CATALOG_INVALID' || this.catalog.error === 'CATALOG_UNAVAILABLE') return false;
    try {
      const p = await this.api.getPrediction(id);
      if (p.status !== 'succeeded' || !p.result_url) return false;
      if (!this.matchesPrediction(p)) return false;
      if (!p.style) return false;
      const blob = await this.api.fetchResult(p.result_url);
      // Never relabel an unknown/deleted look as the default watercolor.
      const styleId = p.style;
      await rememberIllustration({
        id,
        styleId,
        labelSnapshot: p.style_label ?? this.lookLabel(styleId),
        ...this.scope,
        ...(p.catalog_revision ? { revision: p.catalog_revision } : {}),
        width: p.result_width ?? 0,
        height: p.result_height ?? 0,
        createdAt: Date.now(),
        blob,
      });
      await this.refreshLibrary();
      return true;
    } catch {
      return false;
    }
  }

  /** Zet een bewaarde illustratie op het kaartje, zonder nieuwe generatie. */
  async useSaved(id: string): Promise<void> {
    if (this.catalog.error === 'CATALOG_INVALID' || this.catalog.error === 'CATALOG_UNAVAILABLE') return;
    let saved: LibraryRecord | undefined;
    try {
      saved = await getIllustration(id, this.scope);
    } catch {
      saved = undefined;
    }
    if (!saved || !matchesLibraryScope(saved, this.scope)) return;
    track('retry', 'saved');
    const result: ResultImage = {
      blob: saved.blob,
      url: URL.createObjectURL(saved.blob),
      width: saved.width,
      height: saved.height,
    };
    await this.applyResult(result, undefined);
  }

  private async refreshLibrary(): Promise<void> {
    let rows: LibraryRecord[] = [];
    try {
      rows = (await listIllustrations(this.scope)).filter((row) => matchesLibraryScope(row, this.scope));
    } catch {
      rows = [];
    }
    for (const url of this.previewUrls) URL.revokeObjectURL(url);
    this.previewUrls = [];
    const library = rows.map((row) => {
      const previewUrl = URL.createObjectURL(row.blob);
      this.previewUrls.push(previewUrl);
      return {
        id: row.id,
        styleId: row.styleId,
        label: row.labelSnapshot ?? this.lookLabel(row.styleId),
        width: row.width,
        height: row.height,
        createdAt: row.createdAt,
        previewUrl,
      };
    });
    this.set({ library });
  }

  // ------------------------------------------------------------------ openen/sluiten

  open(): void {
    if (this.state.open) return;
    track('open');
    try {
      history.pushState({ ...(history.state as object | null), __aps: this.scope }, '');
      this.historyPushed = true;
    } catch {
      this.historyPushed = false;
    }
    this.set({ open: true, step: { name: 'intro' } });
    void this.refreshLibrary();
    // Nonce alvast ophalen terwijl de klant een foto kiest (zoals popcarte).
    if (!this.availabilityError) {
      queueMicrotask(() => void this.ensureNonce().catch((e) => log('nonce vooraf mislukt', e)));
    }
  }

  close(): void {
    if (!this.state.open) return;
    track('close', this.state.step.name);
    if (this.historyPushed) {
      this.historyPushed = false;
      history.back();
    }
    this.closeInternal();
  }

  private closeInternal(): void {
    this.cancelWork(false);
    this.releaseStep(this.state.step);
    this.turnstile?.destroy();
    this.turnstile = null;
    this.set({ open: false, step: { name: 'intro' } });
  }

  // ------------------------------------------------------------------ acties vanuit de UI

  async pickFile(file: File): Promise<void> {
    this.cancelWork(false);
    if (this.availabilityError) {
      this.releaseStep(this.state.step);
      this.set({ step: { name: 'error', code: this.availabilityError, retry: 'pick' } });
      return;
    }
    const photoReadId = this.photoReadId;
    this.releaseStep(this.state.step);
    this.set({ step: { name: 'preparing' } });
    let photo: PreparedPhoto;
    try {
      photo = await preparePhoto(file);
    } catch (err) {
      if (photoReadId !== this.photoReadId) return;
      const e = toEmbedError(err);
      track('transformation_error', e.code);
      this.set({ step: { name: 'error', code: e.code, retry: 'pick' } });
      return;
    }
    if (photoReadId !== this.photoReadId) {
      URL.revokeObjectURL(photo.url);
      return;
    }
    track('photo_selected', `${photo.width}x${photo.height}`);
    if (this.catalog.fixedLook) {
      await this.generate(photo, this.catalog.fixedLook);
      return;
    }
    this.set({ step: { name: 'style', photo } });
  }

  /** Start de generatie pas nadat de klant een stijl of scène heeft gekozen. */
  async chooseStyle(styleId: string): Promise<void> {
    const step = this.state.step;
    if (step.name !== 'style') return;
    await this.generate(step.photo, styleId);
  }

  /** Nieuwe poging met dezelfde foto (vanuit de vergelijkstap of een foutmelding). */
  async retrySame(): Promise<void> {
    const step = this.state.step;
    if (step.name === 'compare') {
      track('retry');
      URL.revokeObjectURL(step.result.url);
      await this.generate(step.photo, step.styleId);
    } else if (step.name === 'error') {
      track('retry', step.code);
      if (step.retry === 'apply' && step.result) {
        await this.applyResult(step.result, step.photo);
      } else if (step.retry === 'generate' && step.photo && step.styleId) {
        await this.generate(step.photo, step.styleId);
      } else {
        this.set({ step: { name: 'intro' } });
      }
    }
  }

  /** Terug naar het begin; de UI opent direct de bestandskiezer (moet binnen de klik gebeuren). */
  chooseOther(): void {
    track('other_photo');
    this.cancelWork(false);
    this.releaseStep(this.state.step);
    this.set({ step: { name: 'intro' } });
  }

  cancel(): void {
    const step = this.state.step;
    this.cancelWork(true);
    if (step.name === 'working') URL.revokeObjectURL(step.photo.url);
    this.set({ step: { name: 'intro' } });
  }

  async apply(): Promise<void> {
    const step = this.state.step;
    if (step.name !== 'compare') return;
    await this.applyResult(step.result, step.photo);
  }

  private async applyResult(result: ResultImage, photo: PreparedPhoto | undefined): Promise<void> {
    this.set({ step: { name: 'applying', ...(photo ? { photo } : {}), result } });
    try {
      // Eerst het origineel (volle kwaliteit voor druk). Alleen als deze KC-omgeving hem te groot
      // vindt (413, lokaal 1 MB), comprimeren tot onder de 0,9 MB en opnieuw proberen.
      let uploaded: kc.UploadedImage;
      try {
        uploaded = await kc.uploadToImgbase(result.blob);
      } catch (err) {
        if (!(err instanceof EmbedError && err.code === 'IMAGE_TOO_LARGE')) throw err;
        log('KC vond de aquarel te groot (413), comprimeren en opnieuw');
        const small = await compressForUpload(result.blob);
        uploaded = await kc.uploadToImgbase(small.blob);
      }
      const target = await kc.applyUploaded(this.coid, uploaded, this.scope);
      kc.rememberResult(uploaded, this.scope);
      track('apply_success');
      if (photo) URL.revokeObjectURL(photo.url);
      URL.revokeObjectURL(result.url);
      const page = Number(target.pageKey.slice(1)) || 1;
      this.set({
        applied: true,
        step: { name: 'done', cardPreviewUrl: kc.flatPreviewUrl(this.coid, page, 'static_foil', 1024, Date.now()) },
      });
    } catch (err) {
      const e = toEmbedError(err);
      const code: ErrorCode = e.code === 'NO_FRAME' || e.code === 'NETWORK' ? e.code : 'KC_APPLY_FAILED';
      track('apply_error', code);
      this.set({ step: { name: 'error', code, photo, result, retry: 'apply' } });
    }
  }

  // ------------------------------------------------------------------ generatie

  private async generate(photo: PreparedPhoto, styleId: string): Promise<void> {
    const allowed = this.catalog.fixedLook
      ? styleId === this.catalog.fixedLook
      : this.choices.some((look) => look.id === styleId);
    if (this.availabilityError || !allowed) {
      this.set({ step: { name: 'error', code: this.availabilityError ?? 'CATALOG_LOOK_UNAVAILABLE', photo, styleId, retry: 'pick' } });
      return;
    }
    const work = new AbortController();
    this.work = work;
    const startedAt = Date.now();
    setTrackedStyle(styleId);
    this.set({ step: { name: 'working', photo, styleId, phase: 'uploading', startedAt } });
    track('transformation_start');
    try {
      const created = await this.createPrediction(photo, styleId, work.signal);
      this.predictionId = created.prediction_id;
      if (this.state.step.name === 'working') {
        this.set({ step: { name: 'working', photo, styleId, phase: 'painting', startedAt } });
      }
      const final = await this.poll(created, work.signal);
      if (final.status !== 'succeeded' || !final.result_url) {
        throw new EmbedError(final.error ?? (final.status === 'expired' ? 'GENERATION_TIMEOUT' : 'GENERATION_FAILED'));
      }
      if (!this.matchesPrediction(final)) throw new EmbedError('NOT_FOUND');
      const blob = await this.api.fetchResult(final.result_url, work.signal);
      const result: ResultImage = {
        blob,
        url: URL.createObjectURL(blob),
        width: final.result_width ?? photo.width,
        height: final.result_height ?? photo.height,
      };
      if (work.signal.aborted) {
        URL.revokeObjectURL(result.url);
        return;
      }
      await rememberIllustration({
        id: created.prediction_id,
        styleId,
        labelSnapshot: final.style_label ?? this.lookLabel(styleId),
        ...this.scope,
        ...((final.catalog_revision ?? this.catalog.catalog?.revision)
          ? { revision: final.catalog_revision ?? this.catalog.catalog?.revision } : {}),
        width: result.width,
        height: result.height,
        createdAt: Date.now(),
        blob: result.blob,
      });
      await this.refreshLibrary();
      if (work.signal.aborted) {
        URL.revokeObjectURL(result.url);
        return;
      }
      track('transformation_success', Math.round((Date.now() - startedAt) / 1000));
      this.set({ step: { name: 'compare', photo, styleId, result } });
    } catch (err) {
      const e = toEmbedError(err);
      if (e.code === 'CANCELED' || work.signal.aborted) return;
      if (e.code === 'CATALOG_CHANGED') this.catalogStale = true;
      log('generatie mislukt', e.code);
      track('transformation_error', e.code);
      this.set({ step: { name: 'error', code: e.code, retryAfterS: e.retryAfterS, photo, styleId, retry: 'generate' } });
    } finally {
      if (this.work === work) {
        this.work = null;
        this.predictionId = null;
      }
    }
  }

  private matchesPrediction(prediction: PredictionResponse & { configId?: string }): boolean {
    return matchesLibraryScope({
      shopId: prediction.shopId,
      environmentId: prediction.environmentId,
      configId: prediction.configId,
      revision: prediction.catalog_revision,
    }, this.scope);
  }

  private async createPrediction(
    photo: PreparedPhoto,
    styleId: string,
    signal: AbortSignal,
  ): Promise<CreatePredictionResponse> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const nonce = await this.ensureNonce();
      if (signal.aborted) throw new EmbedError('CANCELED');
      try {
        const created = await this.api.createPrediction(nonce, this.sessionId, styleId, photo.blob, this.coid, signal);
        if (this.nonce) this.nonce.usesLeft -= 1;
        return created;
      } catch (err) {
        const e = toEmbedError(err);
        const nonceProblem = e.code.startsWith('NONCE_') || e.code === 'SESSION_MISMATCH';
        if (!nonceProblem || attempt === 1) throw e;
        log('nonce ongeldig, nieuwe ophalen', e.code);
        this.nonce = null;
      }
    }
    throw new EmbedError('INTERNAL');
  }

  private async poll(created: CreatePredictionResponse, signal: AbortSignal): Promise<PredictionResponse> {
    const started = Date.now();
    let delay = created.poll_after_ms > 0 ? created.poll_after_ms : POLL_INTERVAL_MS;
    for (;;) {
      await sleep(delay, signal);
      const p = await this.api.getPrediction(created.prediction_id, signal);
      if (isTerminal(p.status)) return p;
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        void this.api.cancelPrediction(created.prediction_id);
        throw new EmbedError('GENERATION_TIMEOUT');
      }
      delay = POLL_INTERVAL_MS;
    }
  }

  /** Geldige nonce met minstens één gebruik over; anders via Turnstile een nieuwe. */
  private ensureNonce(): Promise<string> {
    const n = this.nonce;
    if (n && n.usesLeft > 0 && n.expiresAt - Date.now() > 15_000) return Promise.resolve(n.value);
    if (!this.noncePromise) {
      this.noncePromise = (async () => {
        const turnstile = this.getTurnstile();
        const token = await turnstile.getToken();
        const res = await this.api.createNonce(token, this.sessionId);
        turnstile.reset(); // tokens zijn eenmalig; alvast een nieuwe klaarzetten
        this.nonce = { value: res.nonce, usesLeft: res.uses_left, expiresAt: Date.parse(res.expires_at) };
        log('nonce ontvangen, gebruiken over:', res.uses_left);
        return res.nonce;
      })().finally(() => {
        this.noncePromise = null;
      });
    }
    return this.noncePromise;
  }

  private getTurnstile(): TurnstileSession {
    if (!this.turnstile) {
      const container = this.turnstileContainer ?? document.body.appendChild(document.createElement('div'));
      this.turnstile = new TurnstileSession(container, this.cfg.turnstileSiteKey);
    }
    return this.turnstile;
  }

  // ------------------------------------------------------------------ intern

  private cancelWork(trackIt: boolean): void {
    this.photoReadId += 1;
    if (!this.work) return;
    if (trackIt) track('cancel');
    this.work.abort();
    this.work = null;
    if (this.predictionId) void this.api.cancelPrediction(this.predictionId);
    this.predictionId = null;
  }

  private releaseStep(step: Step): void {
    if (step.name === 'compare' || step.name === 'applying') {
      if (step.photo) URL.revokeObjectURL(step.photo.url);
      URL.revokeObjectURL(step.result.url);
    } else if (step.name === 'working' || step.name === 'style') {
      URL.revokeObjectURL(step.photo.url);
    } else if (step.name === 'error') {
      if (step.photo) URL.revokeObjectURL(step.photo.url);
      if (step.result) URL.revokeObjectURL(step.result.url);
    }
  }

  private set(partial: Partial<FlowState>): void {
    this.state = { ...this.state, ...partial };
    this.listeners.forEach((fn) => fn());
  }
}

/**
 * Wacht, maar stopt eerder als het tabblad weer zichtbaar wordt: op een telefoon pauzeert de
 * browser timers als je even naar een andere app gaat; bij terugkomst willen we meteen de status.
 */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new EmbedError('CANCELED'));
    const done = () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      signal.removeEventListener('abort', onAbort);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        done();
        resolve();
      }
    };
    const onAbort = () => {
      done();
      reject(new EmbedError('CANCELED'));
    };
    const timer = window.setTimeout(() => {
      done();
      resolve();
    }, ms);
    document.addEventListener('visibilitychange', onVisible);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function newSessionId(): string {
  const key = 'aps:session';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}
